import { useMemo } from "react";
import {
  useCancelTestSuiteRunMutation,
  useCreateTestCaseMutation,
  useCreateTestSuiteMutation,
  useDeleteTestCaseMutation,
  useDeleteTestSuiteMutation,
  useDeleteTestSuiteRunMutation,
  useDuplicateTestCaseMutation,
  useDuplicateTestSuiteMutation,
  useUpdateTestSuiteMutation,
} from "@/data/evals";

/**
 * Hook for all eval mutations (delete, duplicate, cancel, etc.)
 */
export function useEvalMutations({
  isDirectGuest = false,
}: { isDirectGuest?: boolean } = {}) {
  const convexDeleteSuite = useDeleteTestSuiteMutation();
  const convexDeleteRun = useDeleteTestSuiteRunMutation();
  const convexCancelRun = useCancelTestSuiteRunMutation();
  const convexDuplicateSuite = useDuplicateTestSuiteMutation();
  const convexCreateTestCase = useCreateTestCaseMutation();
  const convexDeleteTestCase = useDeleteTestCaseMutation();
  const convexDuplicateTestCase = useDuplicateTestCaseMutation();
  const convexCreateTestSuite = useCreateTestSuiteMutation();
  const updateTestSuiteMutation = useUpdateTestSuiteMutation();

  const mutations = useMemo(() => {
    if (!isDirectGuest) {
      return {
        deleteSuiteMutation: convexDeleteSuite,
        deleteRunMutation: convexDeleteRun,
        cancelRunMutation: convexCancelRun,
        duplicateSuiteMutation: convexDuplicateSuite,
        createTestCaseMutation: convexCreateTestCase,
        deleteTestCaseMutation: convexDeleteTestCase,
        duplicateTestCaseMutation: convexDuplicateTestCase,
        createTestSuiteMutation: convexCreateTestSuite,
        updateTestSuiteMutation,
      };
    }

    const guestUnsupported = async () => {
      throw new Error("Not available for guests yet. Sign in to use this.");
    };

    return {
      deleteSuiteMutation: convexDeleteSuite,
      deleteRunMutation: guestUnsupported,
      cancelRunMutation: guestUnsupported,
      duplicateSuiteMutation: guestUnsupported,
      createTestCaseMutation: convexCreateTestCase,
      deleteTestCaseMutation: convexDeleteTestCase,
      duplicateTestCaseMutation: guestUnsupported,
      createTestSuiteMutation: convexCreateTestSuite,
      updateTestSuiteMutation,
    };
  }, [
    isDirectGuest,
    convexDeleteSuite,
    convexDeleteRun,
    convexCancelRun,
    convexDuplicateSuite,
    convexCreateTestCase,
    convexDeleteTestCase,
    convexDuplicateTestCase,
    convexCreateTestSuite,
    updateTestSuiteMutation,
  ]);

  return mutations;
}
